/**
 * Работа с правами (RBAC) на клиенте — ТОЛЬКО для UX (скрыть/задизейблить контролы).
 * Безопасность обеспечивает сервер (JwtAuthGuard + PermissionsGuard); клиентская проверка
 * не является границей доверия.
 *
 * Права приходят в ответе POST /api/auth/embed внутри user.permissions:
 *   'view' | 'edit' | 'edit_paid' | 'manage_settings'  (permission.enum.ts).
 *
 * Состояние авторизации хранится в лёгком zustand-сторе (заполняется useAuth).
 */

import { create } from 'zustand';
import type { AuthUser, PaymentStatus, Permission, UserRole } from './types';

interface AuthState {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
}

/** Стор авторизованного пользователя (роль + permissions). */
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),
}));

export interface PermissionsApi {
  role: UserRole | null;
  permissions: Permission[];
  /** Есть ли конкретное право. */
  can: (permission: Permission) => boolean;
  /** Может просматривать. */
  canView: boolean;
  /** Может создавать/менять/удалять транши и транзакции. */
  canEdit: boolean;
  /** Может менять уже оплаченные (PAID/PARTIALLY_PAID) или имеющие транзакции транши. */
  canEditPaid: boolean;
  /** Может менять настройки виджета / завершать настройку плана. */
  canManageSettings: boolean;
}

/**
 * Хук прав текущего пользователя. Пример: `const { can, canEdit } = usePermissions();`.
 */
export function usePermissions(): PermissionsApi {
  const user = useAuthStore((s) => s.user);
  const permissions = user?.permissions ?? [];
  const can = (permission: Permission): boolean => permissions.includes(permission);
  return {
    role: user?.role ?? null,
    permissions,
    can,
    canView: can('view'),
    canEdit: can('edit'),
    canEditPaid: can('edit_paid'),
    canManageSettings: can('manage_settings'),
  };
}

/**
 * Единый предикат «замка оплаченного» — повторяет серверный PaidLock (permission.enum + payments.service):
 * транш read-only, если он PAID/PARTIALLY_PAID ИЛИ имеет хотя бы одну транзакцию,
 * и у пользователя нет права edit_paid.
 *
 * Так как payment.serializer не отдаёт отдельного флага hasTransactions, наличие транзакций
 * определяем по paidAmountMinor !== "0" (любая проведённая сумма) ИЛИ по переданному признаку.
 */
export function isPaidLocked(
  payment: { status: PaymentStatus; paidAmountMinor: string; transactions?: unknown[] },
  canEditPaid: boolean,
): boolean {
  if (canEditPaid) return false;
  const hasTransactions =
    (Array.isArray(payment.transactions) && payment.transactions.length > 0) ||
    payment.paidAmountMinor !== '0';
  return payment.status === 'PAID' || payment.status === 'PARTIALLY_PAID' || hasTransactions;
}
